import React, { useState } from 'react';
import { Button, Upload, Modal, Typography, message } from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  FileOutlined,
  IdcardOutlined,
  FileProtectOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import css from './UploadDocumentButton.module.css';

const { Text } = Typography;

/**
 * UploadDocumentButton
 *
 * Renders a button that opens a modal for uploading supporting documents
 * (identity or work authorization). Manages its own modal/loading state;
 * the parent retains the documents array and the ref-synced setter so the
 * documents can be passed to the apply transaction.
 *
 * Props:
 *  modalTitle          – Modal heading text
 *  description         – Short description shown inside the modal
 *  buttonLabel         – Text shown when no files are uploaded yet
 *  uploadedLabel       – Prefix shown when files are uploaded, e.g. "ID Uploaded"
 *  documents           – Current document array from parent state
 *  setDocumentsWithRef – Ref-synced setter (supports functional updates)
 *  onUploadBlobFile    – Async fn({ id, file }) → { blobUrl, blobName, fileName }
 *  buttonType          – AntD button type, default 'default'
 *  buttonSize          – AntD button size, default 'large'
 */
const UploadDocumentButton = ({
  modalTitle,
  description,
  buttonLabel,
  uploadedLabel,
  documents,
  setDocumentsWithRef,
  onUploadBlobFile,
  isOpen,
  onOpenChange,
  onBeforeOpen,
  submitLabel,
  submitDisabled,
  submitLoading,
  onSubmit,
  buttonType = 'default',
  buttonSize = 'large',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const modalOpen = typeof isOpen === 'boolean' ? isOpen : showModal;
  const setModalOpen = nextOpen => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(nextOpen);
    } else {
      setShowModal(nextOpen);
    }
  };

  const handleOpen = async () => {
    if (typeof onBeforeOpen === 'function') {
      try {
        const shouldOpen = await onBeforeOpen();
        if (!shouldOpen) {
          return;
        }
      } catch (error) {
        return;
      }
    }

    setModalOpen(true);
  };

  const allUploaded = documents.length > 0 && documents.every(f => f.url);
  const hasUploadedDocs = documents.some(f => f.url);
  const hasPendingUploads = documents.some(f => f.status === 'uploading');
  const titleText = typeof modalTitle === 'string' ? modalTitle : 'Upload Documents';
  const normalizedTitle = titleText.toLowerCase();
  const isIdentityModal = normalizedTitle.includes('identity');
  const isWorkModal = normalizedTitle.includes('work');
  const headerIcon = isIdentityModal ? <IdcardOutlined /> : isWorkModal ? <FileProtectOutlined /> : <FileOutlined />;
  const showSubmit = typeof onSubmit === 'function';
  const effectiveSubmitDisabled =
    typeof submitDisabled === 'boolean'
      ? submitDisabled
      : !hasUploadedDocs || hasPendingUploads;

  const handleSubmit = async () => {
    if (!showSubmit) {
      return;
    }

    try {
      const shouldClose = await onSubmit();
      if (shouldClose !== false) {
        setModalOpen(false);
      }
    } catch (error) {
      // Keep the modal open on submit failure.
    }
  };

  return (
    <>
      <Button
        style={{ fontSize: 15 }}
        type={buttonType}
        size={buttonSize}
        icon={allUploaded ? <CheckCircleOutlined /> : <UploadOutlined />}
        onClick={handleOpen}
      >
        {allUploaded ? `${uploadedLabel} (${documents.length})` : buttonLabel}
      </Button>

      <Modal
        title={
          <div className={css.uploadDocModalTitleWrap}>
            <div className={css.uploadDocModalTitleIcon}>{headerIcon}</div>
            <div className={css.uploadDocModalTitleTextBlock}>
              <div className={css.uploadDocModalTitle}>{titleText}</div>
              <div className={css.uploadDocModalSubtitle}>
                {documents.length} {documents.length === 1 ? 'file' : 'files'} selected
              </div>
            </div>
          </div>
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={620}
        centered
        wrapClassName={css.uploadDocModalWrap}
        footer={
          showSubmit
            ? [
              <Button key="cancel" className={css.uploadDocCancelBtn} onClick={() => setModalOpen(false)}>
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                className={css.uploadDocSubmitBtn}
                onClick={handleSubmit}
                disabled={effectiveSubmitDisabled}
                loading={submitLoading}
              >
                {submitLabel || 'Submit'}
              </Button>,
            ]
            : [
              <Button key="close" className={css.uploadDocCancelBtn} onClick={() => setModalOpen(false)}>
                Close
              </Button>,
            ]
        }
      >
        <div className={css.uploadDocDescriptionWrap}>
          <Text>{description}</Text>
          <p className={css.uploadDocHintText}>
            Accepted formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB per file, up to 5 files)
          </p>
        </div>

        {documents.length > 0 && (
          <div className={css.uploadDocFileList}>
            {documents.map(f => (
              <div
                key={f.uid}
                className={css.uploadDocFileRow}
              >
                <div className={css.uploadDocFileLeft}>
                  <span className={css.uploadDocFileBadge}>{headerIcon}</span>
                  <Text className={css.uploadDocFileName}>{f.name}</Text>
                </div>
                <div className={css.uploadDocFileActions}>
                  {f.status === 'uploading' ? (
                    <LoadingOutlined className={css.uploadDocFileStatusUploading} />
                  ) : (
                    <CheckCircleOutlined className={css.uploadDocFileStatusDone} />
                  )}
                <Button
                  size="small"
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  className={css.uploadDocRemoveBtn}
                  onClick={() => setDocumentsWithRef(prev => prev.filter(x => x.uid !== f.uid))}
                />
                </div>
              </div>
            ))}
          </div>
        )}

        <Upload
          showUploadList={false}
          beforeUpload={async file => {
            const isValidType = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'].some(ext =>
              file.name.toLowerCase().endsWith(ext)
            );
            if (!isValidType) {
              message.error('Only PDF, JPG, PNG, DOC, DOCX files are allowed!');
              return false;
            }

            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
              message.error('File must be smaller than 10MB!');
              return false;
            }

            const tempId = `${file.name}_${Date.now()}`;
            setDocumentsWithRef(prev => [
              ...prev,
              { uid: tempId, name: file.name, status: 'uploading', percent: 0 },
            ]);

            setUploading(true);
            try {
              const responseData = await onUploadBlobFile({ id: tempId, file });
              if (responseData?.blobUrl) {
                setDocumentsWithRef(prev =>
                  prev.map(f =>
                    f.uid === tempId
                      ? {
                        ...f,
                        status: 'done',
                        url: responseData.blobUrl,
                        blobName: responseData.blobName,
                        uploadedAt: new Date().toISOString(),
                      }
                      : f
                  )
                );
              } else {
                throw new Error('No blobUrl returned');
              }
            } catch (error) {
              message.error(`${file.name} upload failed`);
              setDocumentsWithRef(prev => prev.filter(f => f.uid !== tempId));
            } finally {
              setUploading(false);
            }
            return false;
          }}
          onRemove={file => {
            setDocumentsWithRef(prev => prev.filter(f => f.uid !== file.uid));
          }}
          maxCount={5}
        >
          <Button icon={<UploadOutlined />} loading={uploading} className={css.uploadDocSelectBtn}>
            Select Files
          </Button>
        </Upload>
      </Modal>
    </>
  );
};

export default UploadDocumentButton;
